<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "How much money went through this account, and which entries made it?"
  Answer:   the account's name (the one top-step element), then the flow figures for the
            chosen period - entries, money in, money out, transfers, adjustments, net - and the
            entries behind them.
  Context:  class, type, own currency and tree path under the name; the balance, only where the
            account has its own currency, in that currency; the currency the flow figures are
            stated in; the per-period net bars.
  Controls: the period selector, the link to the Transactions screen and one overflow menu
            for the account's maintenance actions. None is action-coloured on this page.
-->
<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { AccountIcon, ActionIcon, ErrorState } from '@taler/ui'
import DetailNotFound from '@/app/components/DetailNotFound.vue'
import {
  formatLocalDate,
  isZeroMoney,
  type Locale,
  subtractMoney,
  systemClock,
  todayUtc,
} from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  AccountActionsMenu,
  AccountArchiveDialog,
  AccountCreateDialog,
  AccountEditDialog,
  AccountEntryList,
  AccountFlowBars,
  AccountFlowCards,
  AccountReconciliation,
  AccountSubtreePanel,
  canArchiveAccount,
  canCreateAccount,
  canEditAccount,
  canHideAccount,
  canViewAccountBalances,
  canViewAccounts,
  FLOW_PERIODS,
  type FlowPeriod,
  homeCurrencyOf,
  isReconcilableAccount,
  parseFlowPeriod,
  periodRange,
  subtreeIds,
  useAccountBalances,
  useAccountDetail,
  useAccountEntries,
  useAccountFlows,
  useAccountMaintenance,
  useAccountsTree,
  useNativeAccountBalances,
} from '@/features/accounts'
import { canCreateEntries, useDefaultPaymentAccount } from '@/features/entries'
import { canReconcile as canReconcileCapability, useOpenReconciliation } from '@/features/reconciliation'
import { plainAmount, useAccountingView } from '@/app/accounting-view'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const session = useSession()
const { displayCurrency } = useDisplayCurrency()

const accountId = computed(() => String(route.params['accountId'] ?? ''))
const displayLocale = computed<Locale>(() => (locale.value === 'hu' ? 'hu' : 'en'))
const ledgerId = computed(() => session.activeLedger.value?.id)
const canView = computed(() => canViewAccounts(session.can))
const canViewBalances = computed(() => canViewAccountBalances(session.can))
const { accountingView } = useAccountingView()
const enabled = computed(() => canView.value && ledgerId.value !== undefined)

const period = computed<FlowPeriod>(() => parseFlowPeriod(route.query['period']))
const range = computed(() => periodRange(period.value, todayUtc(systemClock)))

function choosePeriod(next: FlowPeriod): void {
  void router.replace({ query: { ...route.query, period: next === 'month' ? undefined : next } })
}

const tree = useAccountsTree({
  ledgerId: () => ledgerId.value,
  locale: () => displayLocale.value,
  enabled: () => canView.value && ledgerId.value !== undefined,
})
const maintenance = useAccountMaintenance(tree)
const row = computed(() => tree.allRows.value.find((candidate) => candidate.id === accountId.value))
const subAccounts = computed(() =>
  tree.allRows.value.filter((candidate) => candidate.parentId === accountId.value),
)
const hasSubAccounts = computed(() => subAccounts.value.length > 0)

const canCreate = computed(() => canCreateAccount(session.can))
const canEdit = computed(() => canEditAccount(session.can))
const canArchive = computed(() => canArchiveAccount(session.can))
const canHide = computed(() => canHideAccount(session.can))
const canSetDefault = computed(() => canCreateEntries(session.can))
const defaultAccountId = computed(() => session.activeLedger.value?.defaultPaymentAccountId)
const defaultPaymentAccount = useDefaultPaymentAccount({ ledgerId: () => ledgerId.value })
const canReconcile = computed(() => canReconcileCapability(session.can))
const openReconciliation = useOpenReconciliation({
  ledgerId: () => ledgerId.value,
  enabled: () => canReconcile.value,
})

const subtree = useAccountBalances({
  ledgerId: () => ledgerId.value,
  displayCurrency: () => displayCurrency.value,
  until: () => formatLocalDate(todayUtc(systemClock)),
  enabled: () => enabled.value && canViewBalances.value,
})

const detail = useAccountDetail({
  ledgerId: () => ledgerId.value,
  accountId: () => accountId.value,
  locale: () => displayLocale.value,
  enabled: () => enabled.value,
})

const account = computed(() => detail.account.value)
const isPostable = computed(() => account.value?.isPostable === true)

const nativeBalances = useNativeAccountBalances({
  ledgerId: () => ledgerId.value,
  accounts: () => {
    const current = row.value
    const home = current === undefined ? undefined : homeCurrencyOf(current)
    return current === undefined || home === undefined
      ? []
      : [{ id: current.id, accountClass: current.accountClass, currency: home }]
  },
  until: () => formatLocalDate(todayUtc(systemClock)),
  enabled: () => enabled.value && canViewBalances.value,
})

const flows = useAccountFlows({
  ledgerId: () => ledgerId.value,
  accountId: () => accountId.value,
  range: () => range.value,
  displayCurrency: () => displayCurrency.value,
  enabled: () => enabled.value && canViewBalances.value,
})

const includeDescendants = computed(() => account.value !== undefined && !isPostable.value)
const subtreeReady = computed(
  () => !includeDescendants.value || (!tree.isLoading.value && tree.error.value === null),
)
const entryAccountIds = computed<ReadonlySet<string>>(() =>
  includeDescendants.value ? subtreeIds(tree.allRows.value, accountId.value) : new Set([accountId.value]),
)

const entries = useAccountEntries({
  ledgerId: () => ledgerId.value,
  accountId: () => accountId.value,
  includeDescendants: () => includeDescendants.value,
  range: () => range.value,
  displayCurrency: () => displayCurrency.value,
  enabled: () => enabled.value && account.value !== undefined && subtreeReady.value,
})
const subtreeFailed = computed(() => includeDescendants.value && tree.error.value !== null)
const entriesError = computed(() => (subtreeFailed.value ? tree.error.value : entries.error.value))
const entriesLoading = computed(
  () => entries.isLoading.value || (includeDescendants.value && tree.isLoading.value),
)
function retryEntries(): void {
  if (subtreeFailed.value) tree.retry()
  else entries.retry()
}
const entriesRetrying = computed(() => (subtreeFailed.value ? tree.retrying.value : entries.retrying.value))

const label = computed(() => detail.labels.value.get(accountId.value))
const pathLabel = computed(() => (label.value?.path ?? []).join(' › '))
const classLabel = computed(() =>
  account.value === undefined ? '' : t(`accounts.classes.${account.value.accountClass}`),
)
const typeLabel = computed(() =>
  account.value === undefined ? '' : t(`accounts.types.${account.value.type}`),
)
const ownCurrencyLabel = computed(() => account.value?.currency ?? t('accounts.noCurrency'))

const periodsAscending = computed(() =>
  [...(flows.flows.value?.periods ?? [])]
    .sort((a, b) => a.periodStart.epochDay - b.periodStart.epochDay)
    .map((period) => ({
      ...period,
      net: plainAmount(period.net, account.value?.accountClass, accountingView.value),
    })),
)
const granularity = computed(() => (period.value === 'all' ? 'YEAR' : 'MONTH'))
const periodLabels = computed<Record<FlowPeriod, string>>(() => ({
  month: t('accountDetail.period.month'),
  quarter: t('accountDetail.period.quarter'),
  year: t('accountDetail.period.year'),
  all: t('accountDetail.period.all'),
}))
const balance = computed(() => {
  const value = nativeBalances.byAccount.value.get(accountId.value)
  if (value === undefined || account.value === undefined) return undefined
  return plainAmount(value, account.value.accountClass, accountingView.value)
})
const currencyNote = computed(() => t('accountDetail.currencyNote', { currency: displayCurrency.value }))

const showReconciliation = computed(
  () => row.value !== undefined && ledgerId.value !== undefined && isReconcilableAccount(row.value),
)
const hasAside = computed(() => hasSubAccounts.value || showReconciliation.value)
const isDefault = computed(() => accountId.value === defaultAccountId.value)
const postableNote = computed(() => {
  if (row.value === undefined) return ''
  if (row.value.isGroup) return t('accounts.groupNote')
  if (!row.value.isPostable) return t('accounts.notPostableNote')
  return hasSubAccounts.value ? t('accountDetail.subAccounts.postableNote') : t('accounts.postableNote')
})

const breakdownTitle = computed(() =>
  account.value?.accountClass === 'EXPENSE'
    ? t('accountDetail.subtree.spending')
    : t('accountDetail.subtree.bySubAccount'),
)
const direct = computed(() => {
  const value = subtree.byAccount.value.get(accountId.value)
  if (value === undefined || account.value === undefined) return undefined
  return plainAmount(value.own, account.value.accountClass, accountingView.value)
})
const inSubAccounts = computed(() => {
  const value = subtree.byAccount.value.get(accountId.value)
  if (value === undefined || account.value === undefined) return undefined
  return plainAmount(
    subtractMoney(value.rolledUp, value.own),
    account.value.accountClass,
    accountingView.value,
  )
})
const breakdown = computed(() => {
  const klass = account.value?.accountClass
  if (klass === undefined) return []
  const own = subtree.byAccount.value.get(accountId.value)
  const directSegment =
    own === undefined || !isPostable.value || isZeroMoney(own.own)
      ? []
      : [
          {
            key: 'direct',
            label: t('accountDetail.subtree.directSegment', {
              name: label.value?.leaf ?? account.value?.code ?? '',
            }),
            money: plainAmount(own.own, klass, accountingView.value),
            to: undefined,
          },
        ]
  const children = subAccounts.value.map((child) => {
    const value = subtree.byAccount.value.get(child.id)
    return {
      key: child.id,
      label: child.label,
      money:
        value === undefined
          ? undefined
          : plainAmount(value.rolledUp, child.accountClass, accountingView.value),
      to: { name: 'account-detail', params: { accountId: child.id } },
    }
  })
  return [...directSegment, ...children]
})
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-[var(--space-section)] p-[var(--space-page)]">
    <RouterLink
      :to="{ name: 'accounts' }"
      class="inline-flex w-fit items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] underline-offset-4 hover:underline"
    >
      <span aria-hidden="true">←</span>{{ t('accountDetail.backToAccounts') }}
    </RouterLink>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('accounts.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('accounts.noAccess') }}
    </p>

    <p v-else-if="detail.isLoading.value" role="status">{{ t('accountDetail.loading') }}</p>

    <DetailNotFound
      v-else-if="detail.notFound.value"
      :to="{ name: 'accounts' }"
      :list="t('accounts.title')"
    />

    <ErrorState
      v-else-if="detail.error.value"
      :title="t('accountDetail.errorTitle')"
      :body="t('accountDetail.errorBody')"
      :retry-label="t('accountDetail.retry')"
      :retrying="detail.retrying.value"
      @retry="detail.retry()"
    />

    <template v-else-if="account !== undefined">
      <div
        class="grid gap-[var(--space-section)]"
        :class="hasAside ? 'xl:grid-cols-[minmax(0,1fr)_20rem]' : ''"
      >
        <header class="flex items-start justify-between gap-[var(--space-group)]">
          <div class="flex min-w-0 items-start gap-[var(--space-group)]">
            <span
              v-if="row"
              aria-hidden="true"
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-surface)]"
              :style="{ backgroundColor: `${row.color}22`, color: row.color }"
            >
              <AccountIcon :icon="row.icon" label="" />
            </span>
            <div class="flex min-w-0 flex-col gap-[var(--space-inline)]">
              <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
                {{ label?.leaf ?? account.code }}
              </h1>
              <p
                class="flex flex-wrap items-center gap-x-[var(--space-group)] gap-y-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
              >
                <span>{{ classLabel }}</span>
                <span>{{ typeLabel }}</span>
                <span>{{ t('accountDetail.ownCurrency') }}: {{ ownCurrencyLabel }}</span>
                <span v-if="pathLabel !== ''">{{ t('accountDetail.path') }}: {{ pathLabel }}</span>
              </p>
              <div v-if="balance !== undefined" class="flex flex-col gap-[var(--space-inline)]">
                <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">{{
                  t('accountDetail.balance')
                }}</span>
                <MoneyAmount
                  :value="balance"
                  size="large"
                  :tone="balance.units < 0n ? 'negative' : undefined"
                />
              </div>
              <p v-if="row" class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
                {{ postableNote }}
              </p>
              <p
                v-if="row?.isArchived"
                class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-attention)]"
              >
                {{ t('accounts.archivedNote') }}
              </p>
              <p
                v-if="row?.isHidden"
                class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
              >
                {{ t('accounts.hiddenNote') }}
              </p>
              <p
                v-if="row?.isSystem"
                class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
              >
                {{ t('accounts.systemAccountNote') }}
              </p>
              <p
                v-if="isDefault"
                class="flex items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]"
              >
                <span class="text-[color:var(--color-attention)]"><ActionIcon name="starFilled" /></span>
                {{ t('accounts.defaultAccountNote') }}
              </p>
            </div>
          </div>

          <AccountActionsMenu
            v-if="row"
            :row="row"
            :can-archive="canArchive"
            :can-create="canCreate"
            :can-edit="canEdit"
            :can-hide="canHide"
            :can-set-default="canSetDefault"
            :is-default="isDefault"
            :is-busy="tree.isCreating.value || defaultPaymentAccount.isSetting.value"
            @archive-request="maintenance.requestArchive($event)"
            @restore="maintenance.restore($event)"
            @add-child="maintenance.openCreate($event)"
            @edit="maintenance.openEdit($event)"
            @hide="tree.hide($event)"
            @unhide="tree.unhide($event)"
            @set-default="defaultPaymentAccount.set($event)"
          />
        </header>
        <aside
          v-if="hasAside"
          class="flex min-w-0 flex-col gap-[var(--space-group)]"
          :aria-label="t('accounts.detailsLabel')"
        >
          <AccountSubtreePanel
            v-if="hasSubAccounts"
            :title="breakdownTitle"
            :direct="direct"
            :in-sub-accounts="inSubAccounts"
            :segments="breakdown"
          />
          <AccountReconciliation
            v-if="showReconciliation && row"
            :ledger-id="ledgerId!"
            :account="row"
            :can-reconcile="canReconcile"
            :open-reconciliation-id="openReconciliation.summary.value?.id"
          />
        </aside>
      </div>

      <div class="flex min-w-0 flex-col gap-[var(--space-section)]">
        <div class="flex flex-wrap items-center justify-between gap-[var(--space-group)]">
          <div
            role="group"
            :aria-label="t('accountDetail.period.label')"
            class="flex flex-wrap gap-[var(--space-inline)]"
          >
            <button
              v-for="option in FLOW_PERIODS"
              :key="option"
              type="button"
              :aria-pressed="period === option"
              class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border px-[var(--space-cell)] text-[length:var(--text-step-2)]"
              :class="
                period === option
                  ? 'border-[color:var(--color-border-control)] bg-[color:var(--color-surface-raised)] text-[color:var(--color-text)]'
                  : 'border-[color:var(--color-border)] text-[color:var(--color-text-muted)]'
              "
              @click="choosePeriod(option)"
            >
              {{ periodLabels[option] }}
            </button>
          </div>
          <RouterLink
            :to="{ name: 'transactions', query: { accountId: accountId } }"
            class="text-[length:var(--text-step-2)] text-[color:var(--color-text)] underline underline-offset-4"
          >
            {{ t('accountDetail.viewInTransactions') }}
          </RouterLink>
        </div>

        <section v-if="canViewBalances" class="flex flex-col gap-[var(--space-inline)]">
          <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ currencyNote }}
          </p>
          <p
            v-if="hasSubAccounts"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('accountDetail.subAccounts.totalsNote') }}
          </p>
          <ErrorState
            v-if="flows.error.value"
            :title="t('accountDetail.errorTitle')"
            :body="t('accountDetail.errorBody')"
            :retry-label="t('accountDetail.retry')"
            :retrying="flows.retrying.value"
            @retry="flows.retry()"
          />
          <AccountFlowCards
            v-else
            :account-class="account?.accountClass"
            :totals="flows.flows.value?.totals"
            :locale="displayLocale"
            :is-loading="flows.isLoading.value"
          />
          <AccountFlowBars
            v-if="periodsAscending.length > 1"
            :periods="periodsAscending"
            :locale="displayLocale"
            :granularity="granularity"
          />
        </section>

        <section class="flex flex-col gap-[var(--space-inline)]">
          <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
            {{ t('accountDetail.entriesTitle') }}
          </h2>
          <p
            v-if="isPostable && hasSubAccounts"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('accountDetail.subAccounts.ownEntriesNote') }}
          </p>
          <AccountEntryList
            v-if="account !== undefined"
            :account-class="account?.accountClass"
            :entries="entries.entries.value"
            :account-ids="entryAccountIds"
            :labels="detail.labels.value"
            :display-currency="displayCurrency"
            :locale="displayLocale"
            :is-loading="entriesLoading"
            :is-fetching-next-page="entries.isFetchingNextPage.value"
            :has-next-page="entries.hasNextPage.value"
            :error="entriesError"
            :retrying="entriesRetrying"
            @load-more="entries.fetchNextPage()"
            @retry="retryEntries()"
          />
        </section>
      </div>
    </template>
    <AccountCreateDialog
      :open="maintenance.createOpen.value"
      :default-parent-id="maintenance.createParentId.value"
      :parent-options="tree.parentOptions.value"
      :is-submitting="tree.isCreating.value"
      :error="maintenance.createError.value"
      @submit="maintenance.submitCreate"
      @close="maintenance.closeCreate()"
    />

    <AccountArchiveDialog
      :open="maintenance.archiveRow.value !== undefined"
      :label="maintenance.archiveRow.value?.label ?? ''"
      :is-submitting="maintenance.isArchiving.value"
      :has-error="maintenance.archiveHasError.value"
      @confirm="maintenance.submitArchive()"
      @close="maintenance.closeArchive()"
    />

    <AccountEditDialog
      :open="maintenance.editOpen.value"
      :row="maintenance.editRow.value"
      :is-submitting="tree.isUpdating.value"
      :has-error="maintenance.updateHasError.value"
      @submit="maintenance.submitUpdate"
      @close="maintenance.closeEdit()"
    />
  </main>
</template>
